/**
 * Web DOM helpers for the guided tour. React Native Web renders `testID` as
 * `data-testid`, so tour targets are stable testIDs, never text or position.
 * The product is web-only (D1); on native these return null and every step
 * shows as a centered card.
 */
import { Platform } from 'react-native';

import type { TourRect, TourTarget } from './tourModel';

const isWeb = Platform.OS === 'web' && typeof document !== 'undefined';

function selectorFor(target: TourTarget): string {
  const prefix = target.endsWith('*');
  const id = (prefix ? target.slice(0, -1) : target).replace(/"/g, '');
  return prefix ? `[data-testid^="${id}"]` : `[data-testid="${id}"]`;
}

function isRendered(el: Element): el is HTMLElement {
  if (!(el instanceof HTMLElement)) return false;
  const rect = el.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return false;
  const style = window.getComputedStyle(el);
  return style.visibility !== 'hidden' && style.display !== 'none' && Number(style.opacity) !== 0;
}

/** The first rendered element among the candidates (in candidate order). */
export function findTourElement(candidates: TourTarget[]): HTMLElement | null {
  if (!isWeb) return null;
  for (const candidate of candidates) {
    const match = Array.from(document.querySelectorAll(selectorFor(candidate))).find(isRendered);
    if (match) return match;
  }
  return null;
}

export function measureTourElement(el: HTMLElement | null): TourRect | null {
  if (!isWeb || !el || !el.isConnected) return null;
  const rect = el.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return null;
  return { x: rect.left, y: rect.top, width: rect.width, height: rect.height };
}

/** Scroll the element into view when it is (partly) off screen. */
export function revealTourElement(el: HTMLElement, reduceMotion: boolean): void {
  if (!isWeb) return;
  const rect = el.getBoundingClientRect();
  const margin = 80;
  const offscreen = rect.top < margin || rect.bottom > window.innerHeight - margin;
  if (offscreen && typeof el.scrollIntoView === 'function') {
    el.scrollIntoView({ block: 'center', inline: 'nearest', behavior: reduceMotion ? 'auto' : 'smooth' });
  }
}

/** Press a control by testID (e.g. a workspace tab) before a step. */
export function pressTourElement(testId: string): boolean {
  const el = findTourElement([testId]);
  if (!el) return false;
  el.click();
  return true;
}

/** Resolves when `check` returns a value or the timeout passes (then null). */
export function pollFor<T>(check: () => T | null, timeoutMs: number, intervalMs = 120): {
  promise: Promise<T | null>;
  cancel: () => void;
} {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let cancelled = false;
  const started = Date.now();
  const promise = new Promise<T | null>((resolve) => {
    const tick = () => {
      if (cancelled) return resolve(null);
      const value = check();
      if (value !== null) return resolve(value);
      if (Date.now() - started >= timeoutMs) return resolve(null);
      timer = setTimeout(tick, intervalMs);
    };
    tick();
  });
  return {
    promise,
    cancel: () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    },
  };
}
